import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Badge from '@atlaskit/badge/badge';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';

import { SuggestionCard } from './SuggestionCard.tsx';
import type { SuggestionItem } from './SuggestionCard.tsx';
import {
	useShowSpaceSuggestionReviewPanel,
	useSpaceSuggestionReviewPanelItem,
} from './review-panel/SuggestionReviewPanelSidebar.tsx';

type NeedsReviewSectionProps = {
	items: SuggestionItem[];
	onReview?: (item: SuggestionItem) => void;
	onApprove?: (item: SuggestionItem) => void;
	onReject?: (item: SuggestionItem) => void;
	onPanelApprove?: (
		item: SuggestionItem,
		acceptedActionStepIds: string[],
		declinedActionStepIds: string[],
	) => void;
	selectedItemId?: string;
};

export function NeedsReviewSection({
	items,
	onReview,
	onApprove,
	onReject,
	onPanelApprove,
}: NeedsReviewSectionProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const { isSpaceSuggestionReviewPanelShown, showSpaceSuggestionReviewPanel } =
		useShowSpaceSuggestionReviewPanel();
	const reviewPanelState = useSpaceSuggestionReviewPanelItem();
	const selectedItem = isSpaceSuggestionReviewPanelShown ? (reviewPanelState?.item ?? null) : null;

	const handleReview = useCallback(
		(item: SuggestionItem) => {
			showSpaceSuggestionReviewPanel(item, (accepted, declined) => {
				onPanelApprove?.(item, accepted, declined);
			});
			onReview?.(item);
		},
		[onReview, onPanelApprove, showSpaceSuggestionReviewPanel],
	);

	return (
		<>
			<Stack space="space.100">
				<Box xcss={styles.title}>
					<Inline alignBlock="center" space="space.100">
						<Heading size="small">{formatMessage(i18n.needsReviewTitle)}</Heading>
						<Badge>{items.length}</Badge>
					</Inline>
				</Box>
				{items.map((item) => (
					<ReviewCard
						key={item.id}
						item={item}
						onReview={handleReview}
						isSelected={selectedItem === item}
						onApprove={onApprove}
						onReject={onReject}
					/>
				))}
			</Stack>
		</>
	);
}

type ReviewCardProps = {
	item: SuggestionItem;
	isSelected: boolean;
	onReview: (item: SuggestionItem) => void;
	onApprove?: (item: SuggestionItem) => void;
	onReject?: (item: SuggestionItem) => void;
};

/**
 * Small wrapper around SuggestionCard that binds the per-item Approve/Reject
 * handlers via `useCallback`. Inlining `() => onApprove(item)` inside the
 * parent's `.map()` would create a new function reference on every render,
 * defeating memoization of any future memoized child and tripping the
 * `@atlassian/perf-linting/detect-unnecessary-rerenders` rule.
 */
function ReviewCard({ item, onReview, onApprove, onReject, isSelected }: ReviewCardProps) {
	const handleApprove = useCallback(() => onApprove?.(item), [onApprove, item]);
	const handleReject = useCallback(() => onReject?.(item), [onReject, item]);
	return (
		<SuggestionCard
			item={item}
			onReview={onReview}
			onApprove={handleApprove}
			onReject={handleReject}
			isSelected={isSelected}
		/>
	);
}

const styles = cssMap({
	title: {
		marginBottom: token('space.050'),
	},
});

const i18n = defineMessages({
	needsReviewTitle: {
		id: 'space-suggestions.needs-review.section-title',
		defaultMessage: 'Needs review',
		description:
			'Title for the "needs review" section. The number of suggestions awaiting review is shown next to it in a badge.',
	},
});
